import React, { useState, useEffect } from 'react';
import styles from '../insertsPages.module.css';
import { Tabs } from '../../../Tabs';
import { Chooser } from '../../../Chooser';
import chooserStyles from '../../../Chooser/chooser.module.css';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Visualiser, VisualiserGroup, VisualiserTextBox } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct, findVariantById } from '../../../../utilities/visualiserVariants.js';

/**
 * CardPredesignedMessage component allows users to choose from pre-designed messages.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function CardPredesignedMessage({ ...rest }) {
  const { getTextInput, setTextInput, setVisualiserSelection, clearVisualiserSelection } = useUserInput();

  const [chooserOpen, setChooserOpen] = useState(true);
  const [frontImage, setFrontImage] = useState(null); // Local state only (not persisted to UserInputContext).
  const [selectedTitle, setSelectedTitle] = useState(getTextInput('cardPredesigned.inside') || '');
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedStockCardId = getTextInput('cardPredesigned.selectedStockCardId');

  // Clear inside text when component mounts if no template is selected.
  // This ensures free-form card text doesn't persist when switching to pre-designed.
  useEffect(() => {
    const insideValue = getTextInput('cardPredesigned.inside');
    if (!selectedStockCardId && insideValue) {
      setTextInput('cardPredesigned.inside', '');
      setSelectedTitle('');
    }
  }, []);

  useEffect(() => {
    const productKey = 'greetingsCards';
    setVisualiserSelection({ productKey, variant: null });

    return () => {
      clearVisualiserSelection();
    };
  }, [setVisualiserSelection, clearVisualiserSelection]);

  useEffect(() => {
    const productKey = 'greetingsCards';
    const product = getInsertsAndCardsProduct({ productKey });
    const parsedId = selectedStockCardId ? Number(selectedStockCardId) : null;
    const variant = findVariantById({ product, variantId: typeof parsedId === 'number' ? parsedId : null });

    // If no variant is selected yet, keep it null so the summary can show "not selected" state.
    setVisualiserSelection({
      productKey,
      variant,
      config: {
        templateId: '',
        mapping: {
          text: {
            'cardPredesigned.inside': 'PersonalisationCard'
          }
        }
      }
    });
  }, [selectedStockCardId, setVisualiserSelection, clearVisualiserSelection]);

  const templates = window.giftWrapData?.insertsAndCards?.products?.greetingsCards;
  if (!templates) return <p>No greetings card configs</p>;

  const variants = Array.isArray(templates?.variants) ? templates.variants : [];

  const handleTemplateClick = ({ image, title, variantId }) => {
    setFrontImage(image);
    setTextInput('cardPredesigned.inside', title);
    setSelectedTitle(title);
    setActiveIndex(0);
    setChooserOpen(false);
    setTextInput('cardPredesigned.selectedStockCardId', String(variantId));
  };

  return (
    <div className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs
          tabs={[
            { id: 'front', label: 'Front' },
            { id: 'inside', label: 'Inside' },
            { id: 'back', label: 'Back' }
          ]}
          activeIndex={activeIndex}
          onChange={setActiveIndex}
          fullWidth={true}
        />

        {activeIndex === 0 && (
          <Visualiser
            type="GREETINGS_CARD"
            background={frontImage ? `url(${frontImage})` : 'var(--color-white)'}
            subtitle={frontImage ? 'Not editable' : 'Choose a design below'}
          >
            {' '}
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <VisualiserGroup>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
              {' '}
            </Visualiser>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Tap to edit">
              <VisualiserTextBox
                size={{
                  width: '190px',
                  minWidth: '150px',
                  maxWidth: '190px',
                  minHeight: '50px',
                  maxHeight: '260px'
                }}
                position={{
                  top: '50%',
                  left: '50%'
                }}
                maxLength={150}
                maxLines={12}
                lineHeight={1.2}
                fontSize={16}
                style={{
                  transform: 'translate(-50%, -50%)'
                }}
                value={getTextInput('cardPredesigned.inside')}
                setValue={((value) => setTextInput('cardPredesigned.inside', value))}
              />
            </Visualiser>
          </VisualiserGroup>
        )}

        {activeIndex === 2 && (
          <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
            {' '}
          </Visualiser>
        )}
      </div>

      {activeIndex === 0 && (
        <Chooser title="Choose a Design" subtitle={selectedTitle || ''} open={chooserOpen} onToggle={setChooserOpen}>
          <div className={chooserStyles['stock-card--chooser-grid']}>
            {variants.map((variant) => {
              return (
                <div
                  key={variant.id}
                  className={chooserStyles['stock-card--chooser-item']}
                  onClick={() =>
                    handleTemplateClick({
                      image: variant.featured_image.src,
                      title: variant.title,
                      variantId: variant.id
                    })
                  }
                >
                  <img src={variant.featured_image.src} />
                  <p>{variant.title}</p>
                </div>
              );
            })}
          </div>
        </Chooser>
      )}
    </div>
  );
}
